
import AccordionBox from '@/components/layout/AccordionBox.vue';
import { dateTimeFormats } from '@/lang/dateTimeFormats';
import { fullName } from '@/lib/employee/employeeFunctions';
import { Icon, IconType } from '@/lib/enum/Icon';
import {
  companyCanAccess,
  Feature,
} from '@/lib/permission/companyAccessFeatures';
import { useUserCan } from '@/lib/permission/userCanComposable';
import { Entity } from '@/lib/realtime/RealtimeEntity';
import { realtimeQuery } from '@/lib/realtime/weak/realtimeFunctions';
import store from '@/store';
import { sortBy } from '@/util/arrayFunctions';
import { formatCurrency } from '@/util/currencyFunctions';
import { getEntity } from '@/util/entityFunctions';
import spacetime from 'spacetime';
import { PropType } from 'vue';
import {
  Employee,
  History,
  HistoryActionEnum,
  HistoryChangesInner,
  JobRole,
  Location,
  Schedule,
  Shift,
  TimesheetEntry,
  TimesheetEntryStatusEnum,
} from '../../../../api/v1';
import Dialog from '../../../components/dialog/Dialog.vue';
import EmployeeAvatar from '../../../components/employee/EmployeeAvatar.vue';
import LoadingDot from '../../../components/loaders/LoadingDot.vue';

type ShiftFieldsType =
  | keyof Shift
  | 'unpaidBreakMins'
  | 'durationInMins'
  | 'allocatedBreakInMinutes';

type CustomHistoryType = {
  date: Date;
  heading: string;
  subHeading: string;
  employee: Employee | null;
  dot: string | null;
  dotColour: string | null;
  dotIcon?: IconType;
  changes: HistoryChangesInner[];
};

export default {
  name: 'ShiftHistoryV2',
  components: {
    LoadingDot,
    EmployeeAvatar,
    Dialog,
    AccordionBox,
  },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    shift: {
      type: Object as PropType<Shift>,
      required: true,
    },
    timesheet: {
      type: Object as PropType<TimesheetEntry | null>,
      default: null,
    },
  },
  data() {
    return {
      Icon,
      userCanWithScope: useUserCan(store.getters.loggedInEmployee),
      employees: realtimeQuery(Entity.Employee).fetch(),
      jobRoles: realtimeQuery(Entity.JobRole).fetch(),
      schedules: realtimeQuery(Entity.Schedule).fetch(),
      // Fetch the recorded history entries from the API
      histories: realtimeQuery(Entity.History)
        .where('foreignId', 'eq', `${this.shift.id}`)
        .where('tableName', 'eq', 'shifts')
        .where('action', 'eq', HistoryActionEnum.Updated)
        .fetch(),
    };
  },
  computed: {
    currencyCode: (): string => store.getters['settings/currencyCode'],
    timezone: (): string => store.getters.timezone,
    locations: (): Location[] => store.state.locations.activeLocations.data,

    open: {
      get(): boolean {
        return this.value;
      },
      set(v: boolean) {
        this.$emit('input', v);
        if (!v) this.$emit('close');
      },
    },

    initialising(): boolean {
      return (
        this.employees.isLoading ||
        this.jobRoles.isLoading ||
        this.schedules.isLoading ||
        this.histories.isLoading
      );
    },

    // Standard shift events that are not recorded as history entries
    customHistories(): CustomHistoryType[] {
      const {
        createdAt,
        creatorId,
        publishedAt,
        publishedById,
        acknowledgedAt,
        employeeId,
      } = this.shift;
      return [
        {
          date: createdAt,
          heading: this.$tc('shiftHistory.shiftCreated'),
          subHeading: this.historySubHeading(creatorId, createdAt),
          employee: this.getEmployee(creatorId),
          dot: null,
          dotColour: null,
          changes: [],
        },
        ...(publishedAt
          ? [
              {
                date: publishedAt,
                heading: this.$tc('history.published'),
                subHeading: this.historySubHeading(publishedById, publishedAt),
                employee: null,
                dot: 'small',
                dotColour: 'bg-green',
                changes: [],
              },
            ]
          : []),
        ...(acknowledgedAt
          ? [
              {
                date: acknowledgedAt,
                heading: this.$tc('history.confirmed'),
                subHeading: this.historySubHeading(employeeId, acknowledgedAt),
                employee: null,
                dot: 'small',
                dotColour: 'bg-gray-200',
                changes: [],
              },
            ]
          : []),
        ...(this.timesheet?.status === TimesheetEntryStatusEnum.Completed
          ? [
              {
                date: this.timesheet.endedAt,
                heading: this.$tc('shiftHistory.shiftCompleted'),
                subHeading: `${this.$d(
                  this.timesheet.endedAt,
                  dateTimeFormats.dayMonthYear,
                )} @ ${this.$d(
                  this.timesheet.endedAt,
                  dateTimeFormats.hourMinute,
                )}`,
                employee: null,
                dot: 'big',
                dotColour: 'bg-green',
                dotIcon: Icon.Check,
                changes: [],
              },
            ]
          : []),
      ];
    },

    allHistories(): CustomHistoryType[] {
      // @todo workout who the shift belongs to at a given point in time
      //  and hide cost history if the viewer can't see the employee costs
      //  for the employee at that point in time
      const fetchedHistories: CustomHistoryType[] = this.histories.data
        .map((history: History) => {
          const { changedAt, changedById, changes } = history;
          return {
            date: changedAt,
            heading: this.$tc('shiftHistory.shiftUpdated'),
            subHeading: this.historySubHeading(changedById, changedAt),
            employee: null,
            dot: 'small',
            dotColour: 'bg-gray-200',
            changes: changes.filter(
              ({ attribute }) =>
                // Published and acknowledged get displayed from the shift data
                ![
                  'publishedAt',
                  'publishedById',
                  'acknowledgedAt',
                  'lockedAt',
                ].includes(attribute) &&
                // Only show cost changes if the company has paid for HR features and the logged in employee has access
                (companyCanAccess(Feature.HrManagement) ||
                  attribute !== 'cost'),
            ),
          };
        })
        .filter((customHistory) => customHistory.changes.length);

      return sortBy([...this.customHistories, ...fetchedHistories], 'date');
    },

    // In some cases history still exists for old column names
    fieldNames(): Partial<Record<ShiftFieldsType, string>> {
      return {
        startsAt: this.$tc('history.shiftStartsAt'),
        endsAt: this.$tc('history.shiftEndsAt'),
        cost: this.$tc('history.shiftCost'),
        /* history.unpaidBreak */
        unpaidBreakMins: this.$tc('history.unpaidBreak'), // unpaidBreakMins was renamed to allocatedBreakInMinutes
        allocatedBreakInMinutes: this.$tc('history.unpaidBreak'), // allocatedBreakInMinutes was split into paidBreakInMinutes and unpaidBreakInMinutes
        unpaidBreakInMinutes: this.$tc('history.unpaidBreak'),
        /* history.durationInMins */
        paidBreakInMinutes: this.$tc('history.paidBreak'),
        jobRoleId: this.$tc('history.jobRole'),
        employeeId: this.$tc('history.employee'),
        scheduleId: this.$tc('history.schedule'),
        locationId: this.$tc('history.location'),
        publishedAt: this.$tc('history.published'),
        notes: this.$tc('history.notes'),
        /* history.durationInMins */
        durationInMins: this.$tc('history.durationInMins'), // durationInMins was renamed to durationInMinutes
        durationInMinutes: this.$tc('history.durationInMins'),
        expectedWorkingTimeInMinutes: this.$tc('history.durationInMins'),
        /* history.durationInMins */
        open: this.$tc('history.open'),
        showJobRole: this.$tc('history.showJobRole'),
        requiresApproval: this.$tc('history.requiresApproval'),
        showEndTime: this.$tc('history.showEndTime'),
      };
    },
  },

  methods: {
    getAssignedEmployeeAtPointInTime(date: Date): Employee | null {
      // Get the history item which changes the employee id
      // Find the employee id of the change at the given dateTime
      // If there are no employee id changes then use the current one
      const latestEmployeeChangeHistory = sortBy(
        this.histories.data.filter(
          (history) =>
            history.changes.some(
              (change) => change.attribute === 'employeeId',
            ) && history.changedAt <= date,
        ),
        '-changedAt',
      )[0];

      const nextEmployeeChangeHistory = sortBy(
        this.histories.data.filter(
          (history) =>
            history.changes.some(
              (change) => change.attribute === 'employeeId',
            ) && history.changedAt > date,
        ),
        'changedAt',
      )[0];

      let assignedEmployeeId = this.shift.employeeId;

      // Always check the last employee change history first
      if (latestEmployeeChangeHistory) {
        const employeeId = latestEmployeeChangeHistory.changes.find(
          (change) => change.attribute === 'employeeId',
        ).newValue;
        assignedEmployeeId = employeeId ? Number(employeeId) : null;
      }
      // In the case where there was no employee change before other changes,
      // we can get the next one to work out the old value
      else if (nextEmployeeChangeHistory) {
        const employeeId = nextEmployeeChangeHistory.changes.find(
          (change) => change.attribute === 'employeeId',
        ).oldValue;
        assignedEmployeeId = employeeId ? Number(employeeId) : null;
      }
      // In case there are no employee changes, return the current assigned employee
      return assignedEmployeeId
        ? getEntity(assignedEmployeeId, this.employees.data)
        : null;
    },
    getEmployee(employeeId: number): Employee | undefined {
      return getEntity(employeeId, this.employees.data);
    },
    getEmployeeName(employeeId: number | null = null): string {
      if (employeeId) {
        const employee = this.getEmployee(employeeId);
        return employee
          ? fullName(employee)
          : this.$tc('enum.employeeStatus.Archived', 1);
      }
      return '';
    },
    getFormattedDateString(date: Date): string {
      return `${this.$d(date, dateTimeFormats.dayMonthYear)} @ ${this.$d(
        date,
        dateTimeFormats.hourMinute,
      )}`;
    },
    getFieldName(field: string): string {
      return this.fieldNames[field] ?? '';
    },

    historySubHeading(employeeId: number | null, date: Date): string {
      return this.$tc('shiftHistory.byOn', 1, {
        employee: employeeId
          ? this.getEmployeeName(employeeId)
          : this.$tc('word.system'),
        date: this.getFormattedDateString(date),
      });
    },

    formatTime(date: Date): string {
      // History dates are given in a utc date and time string, e.g. "2022-07-22 02:15:00"
      // We need to spacetime it as utc then go to the company timezone and give a native date to i18n for formatting
      return this.$d(
        spacetime(date, 'utc').goto(this.timezone).toNativeDate(),
        dateTimeFormats.dayMonthLongYearHourMinute,
      );
    },
    formatValue(val: unknown, field: string, date: Date): string | unknown {
      if (['startsAt', 'endsAt', 'publishedAt'].includes(field)) {
        return this.formatTime(val);
      }
      if (
        [
          'durationInMins',
          'durationInMinutes',
          'expectedWorkingTimeInMinutes',
          'unpaidBreakMins',
          'allocatedBreakInMinutes',
          'unpaidBreakInMinutes',
          'paidBreakInMinutes',
        ].includes(field)
      ) {
        return this.$tc('unitWithAmount.min', Number(val), { n: val });
      }
      if (['cost'].includes(field)) {
        const assignedEmployee = this.getAssignedEmployeeAtPointInTime(date);
        if (
          this.userCanWithScope.viewLabourCosts() &&
          (!assignedEmployee ||
            this.userCanWithScope.viewEmployeeCosts(assignedEmployee))
        ) {
          return formatCurrency(Number(val) || 0, this.currencyCode);
        }
        return this.$tc('placeholder.redacted');
      }
      if (field === 'jobRoleId') {
        return getEntity<JobRole>(Number(val), this.jobRoles.data)?.name ?? '';
      }
      if (field === 'scheduleId') {
        return (
          getEntity<Schedule>(Number(val), this.schedules.data)?.name ?? ''
        );
      }
      if (field === 'employeeId') {
        const employee = this.getEmployee(Number(val));
        return employee
          ? fullName(employee)
          : this.$tc('subHeading.unassigned');
      }
      if (field === 'locationId') {
        return getEntity<Location>(Number(val), this.locations)?.name ?? '';
      }
      if (
        ['open', 'showEndTime', 'requiresApproval', 'showJobRole'].includes(
          field,
        )
      ) {
        return String(val === '1');
      }
      return val;
    },
  },
};
